/**
 * Texts of the online lobby and the online battle (port addition, design D5). As everywhere in the game, the Russian
 * text is the source and the translation key (src/game/runtime/i18n.ts): the game's text fields translate it on
 * assignment. Composed lines are built from fragments that each have a catalogue entry (tools/i18n/extra.json), so a
 * player name, a room code or a number in the middle is kept as it is.
 */
import { translate } from './i18n';

// ---------------------------------------------------------------- the "Онлайн" mode: the lobby on the fight screen
export const MSG_ONLINE = 'Онлайн';
export const MSG_ONLINE_HINT = 'Игра по сети с другими игроками: комнаты и бои в режиме «Просто играть».';
/** The heading of the room list (the task list's heading), followed by the number of listed rooms: " (3)". */
export const MSG_OPEN_ROOMS = 'Открытые комнаты';
export const MSG_MORE_ROOMS = 'Показать ещё комнаты';
export const MSG_NO_ROOMS = 'Открытых комнат пока нет.';
export const MSG_ROOMS_LOADING = 'Загрузка списка комнат…';
export const MSG_ONLINE_UNAVAILABLE = 'Онлайн-игра на этом сервере недоступна.';
/** A room of the list: "<b>Карта №2</b> · Ann · игроки: 1/2" (+ " (полная)"). */
export const MSG_ROOM_PLAYERS = 'игроки: ';
export const MSG_ROOM_IS_FULL = ' (полная)';
/** A team of the picked room nobody plays yet. */
export const MSG_FREE_SLOT = '(свободное место)';
/** The block of the player's own actions below the picked room. */
export const MSG_CREATE_OWN_ROOM = 'Создать свою комнату';
export const MSG_CREATE_ROOM_HINT = 'Вы - хост: выберите карту и команды, другие игроки присоединятся.';
export const MSG_CREATE_OPEN = 'Открытая';
export const MSG_CREATE_PRIVATE = 'Закрытая (с кодом)';
export const MSG_JOIN_PRIVATE = 'Войти в закрытую комнату';
export const MSG_JOIN_BY_CODE = 'Войти по коду';
export const MSG_LOBBY_HINT = 'Выберите комнату в списке справа и нажмите «Играть», чтобы присоединиться. Список обновляется каждые 5 секунд.';
export const MSG_ROOM_NOT_FOUND = 'Комната не найдена.';
export const MSG_ROOM_FULL = 'В комнате нет свободных мест.';
export const MSG_ROOM_CLOSED = 'Комната закрыта.';
export const MSG_ROOM_IN_GAME = 'В этой комнате уже идёт бой.';
/** The room the player picked in the list filled up / left the list since: it is no longer picked. */
export const MSG_PICKED_ROOM_FULL = 'В выбранной комнате не осталось свободных мест. Выберите другую.';
export const MSG_PICKED_ROOM_GONE = 'Выбранной комнаты больше нет в списке: её закрыли или в ней начался бой. Выберите другую.';
export const MSG_ENTER_CODE = 'Введите код комнаты.';
export const MSG_ONLINE_FAILED = 'Не удалось выполнить действие. Попробуйте ещё раз.';

// ---------------------------------------------------------------- the room (menu_just_play in room mode)
export const MSG_ROOM_CODE = 'Код комнаты: ';
export const MSG_PUBLIC_ROOM = 'Открытая комната.';
export const MSG_READY = 'готов';
export const MSG_NOT_READY = 'не готов';
export const MSG_OFFLINE = 'нет связи';
export const MSG_HOST = 'хост';
export const MSG_HOST_HINT = 'Настройте карту и команды. Когда все будут готовы, нажмите кнопку внизу справа.';
export const MSG_GUEST_HINT = 'Нажмите кнопку внизу справа, чтобы подтвердить готовность (ещё раз - отменить).';
export const MSG_WAIT_PLAYERS = 'Нужен хотя бы ещё один игрок.';
export const MSG_WAIT_READY = 'Не все игроки готовы.';
export const MSG_WAIT_CONNECTED = 'Кто-то из игроков не на связи.';
export const MSG_STARTING = 'Бой начинается…';

// ---------------------------------------------------------------- the online battle
export const MSG_OPPONENT_TURN = 'Ход соперника…';
/** The local player's own team in an online battle's HUD and log: "Команда #1 (вы)". */
export const MSG_OWN_TEAM = '(вы)';
/** The note of the weapon sheet in an online battle (the SWF's note says the timer stops while the sheet is open). */
export const MSG_SHEET_TIMER_RUNS = 'В сетевом бою таймер не останавливается, пока открыт арсенал';
export const MSG_OPPONENT_SILENT = 'Соперник не отвечает…';
export const MSG_RESTORING = 'Восстановление боя…';

// ---------------------------------------------------------------- the result (menu_question, mode online_result)
export const MSG_WIN = 'Победа!';
export const MSG_LOSE = 'Поражение';
export const MSG_BATTLE_OVER = 'Бой окончен';
export const MSG_WINNER = 'Победитель: ';
export const MSG_NO_WINNER = 'Победителя нет.';
export const MSG_VERDICT_WAIT = 'Проверка результата сервером…';
export const MSG_VERDICT_OK = 'Результат подтверждён сервером.';
export const MSG_VERDICT_REJECTED = 'Сервер не подтвердил результат: засчитан результат проверки.';
export const MSG_VERDICT_UNVERIFIED = 'Сервер не проверял результат.';
export const MSG_YOU_SURRENDERED = 'Вы покинули бой.';
export const MSG_OPPONENT_LEFT = 'Соперник покинул бой.';
export const MSG_DESYNC = 'Бой разошёлся с соперником (рассинхронизация) и прерван.';
export const MSG_DESYNC_YOURS = 'Проверка сервера: расхождение в вашей игре.';
export const MSG_DESYNC_THEIRS = 'Проверка сервера: расхождение в игре соперника.';
export const MSG_BATTLE_LOST = 'Связь с боем потеряна.';

/*
 * The texts below hold a player's name: they are DISPLAY text (the game's words already translated, the name as
 * typed) and go into their field with setRawText (src/game/ui/controls.ts), so the name is never translated.
 */

/** One line of a room player on the room screen: "Ann (хост, готов)". */
export function roomPlayerText(name: string, host: boolean, ready: boolean, connected: boolean): string {
  const parts: string[] = [];
  if (host) parts.push(MSG_HOST);
  parts.push(ready ? MSG_READY : MSG_NOT_READY);
  if (!connected) parts.push(MSG_OFFLINE);
  return name + translate(' (' + parts.join(', ') + ')');
}

/** A team of the room screen with its player: "Команда #1 (Ann)". */
export function roomTeamText(team: string, player: string | null): string {
  return player === null ? translate(team) : translate(team) + ' (' + player + ')';
}

/** The map of a room of the lobby list: "Карта №2" (in bold in its row). */
export function roomMapText(mapIndex: number): string {
  return translate('Карта №' + mapIndex);
}

/** A room of the lobby list: "Карта №2 · Ann · игроки: 1/2", a full one with " (полная)". */
export function roomRowText(host: string, mapIndex: number, players: number, maxPlayers: number, full: boolean): string {
  return roomMapText(mapIndex) + ' · ' + host + ' · ' + translate(MSG_ROOM_PLAYERS + players + '/' + maxPlayers + (full ? MSG_ROOM_IS_FULL : ''));
}

/** The player of a team of the picked room in the lobby: "Ann", the host "Ann (хост)". */
export function roomTeamPlayerText(name: string, host: boolean): string {
  return host ? name + translate(' (' + MSG_HOST + ')') : name;
}
